import { FloatingPortal } from '@floating-ui/react';
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { onGlobalKeyDown } from '@lumen/platform';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  width?: number | string;
  height?: number | string;
  testId?: string;
  style?: CSSProperties;
  align?: 'top' | 'center';
  children: ReactNode;
}

export function Modal({ open, onClose, width = 720, height, testId, style, align = 'top', children }: ModalProps) {
  const [state, setState] = useState<'opening' | 'open'>('opening');
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) {
      setState('opening');
      return;
    }
    const raf = requestAnimationFrame(() => setState('open'));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        e.preventDefault();
        closeRef.current();
      }
    };
    const off = onGlobalKeyDown(onKey);
    return () => {
      cancelAnimationFrame(raf);
      off();
    };
  }, [open]);
  if (!open) return null;
  return (
    <FloatingPortal>
      <div
        className="lm-overlay"
        data-state={state}
        style={align === 'center' ? { alignItems: 'center' } : undefined}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className="lm-modal"
          role="dialog"
          aria-modal="true"
          data-testid={testId}
          style={{ width, height, ...(align === 'center' ? { marginTop: 0 } : {}), ...style }}
        >
          {children}
        </div>
      </div>
    </FloatingPortal>
  );
}
